import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { showLoadingScreen } from 'a/hub/actions/metaGameActions';
import { reportNavigation } from 'a/hub/actions/metagameSearchlight';
import {
  autoCompleteVocabSession,
  setTransmitterStatus,
  setWorkshopStepAsInProgress,
} from 'a/hub/actions/workshopActions';
import GameIds from 'a/hub/data/GameIds';
import { ACTIVITY_STATUS, REFRESH_TIMEOUT } from 'a/hub/data/constants';
import { getCurrentVocabSessionId } from 'a/hub/selectors';
import Env from 'root/lib/Env';
import {
  DESTINATION_GROUP,
  NAVIGATION_ORIGIN,
  NAVIGATION_TRIGGER_TYPE,
} from 'root/lib/SearchlightDispatcher';
import { getIsWellBelowBenchmark, getIsQuestInDemoMode } from 'a/hub/selectors/workshopSelectors';
import useWorkshopSfx from '../../../hooks/useWorkshopSfx';
import { ModalContainer, AutoCompleteButton } from '../sharedComponents';
import ModalSmallBackground from '../svgs/Common/SmallBackground.svg';
import {
  Background,
  GoButton,
  CloseModalButton,
  Decoline,
  HeaderText,
  VocabFrequencyScanning,
  VocabGuy,
  Wave,
} from './styledComponents';
import DecolineMiddle from './svgs/DecolineMiddle.svg';
import VocabGuyHappy from './svgs/VocabGuyHappy.svg';
import WaveImage from './svgs/Wave.svg';

const VocabLaunchModal = ({ closeModal }) => {
  const season = 3;
  const dispatch = useDispatch();
  const sessionId = useSelector(getCurrentVocabSessionId);
  const questId = useSelector(({ workshopReducer }) => workshopReducer.currentQuestId);
  const isVocabAppAvailable = useSelector(
    ({ workshopReducer }) => workshopReducer.isVocabAppAvailable,
  );
  const isQuestInDemoMode = useSelector(getIsQuestInDemoMode);
  const isVocabAppAvailableInDemoMode = useSelector(
    ({ workshopReducer }) => workshopReducer.isVocabAppAvailableInDemoMode,
  );

  const { playTap, playSfxFromMap, audioMap } = useWorkshopSfx();
  const isWellBelowBenchmark = useSelector(getIsWellBelowBenchmark);

  useEffect(() => {
    playSfxFromMap(audioMap.transmitter_output);
  }, []);

  const launchVocab = async () => {
    const navigationParams = {
      destination: GameIds.VOCAB,
      destinationGroup: DESTINATION_GROUP.VOCAB,
      origin: NAVIGATION_ORIGIN.WORKSHOP,
      trigger: 'vocabLaunchPopup',
      triggerType: NAVIGATION_TRIGGER_TYPE.MANUAL,
    };
    dispatch(reportNavigation({ ...navigationParams }));

    dispatch(showLoadingScreen());
    await dispatch(setWorkshopStepAsInProgress(true));

    // start redirect to Vocab after 3 seconds
    setTimeout(() => {
      const VOCAB_REDIRECT_URL = `${Env.VOCAB_REACT_URL}?externalSource=reading&season=${season}`;
      const grade = isQuestInDemoMode ? '&teacherGrade=4' : '';
      const readAloud = isWellBelowBenchmark ? '&readAloud=on' : '';
      window.location.href = `${VOCAB_REDIRECT_URL}&questId=${sessionId}${grade}${readAloud}`;
    }, REFRESH_TIMEOUT);
  };

  const onCloseButtonPress = () => {
    playTap(true);
    dispatch(setTransmitterStatus(ACTIVITY_STATUS.CALL_TO_ACTION));
    closeModal();
  };

  // we can temporary disable Vocab in Demo mode although in normal mode it still can be enabled
  const isAutoComplete = !sessionId || (isQuestInDemoMode ? !isVocabAppAvailableInDemoMode : !isVocabAppAvailable);

  const onGoButtonPress = () => {
    playTap();
    if (isAutoComplete) {
      dispatch(autoCompleteVocabSession(questId, sessionId));
      closeModal();
    } else {
      dispatch({
        gameId: GameIds.METAGAME,
        type: 'SEARCHLIGHT_DISPATCH_EVENT',
        payload: {
          gameId: GameIds.METAGAME,
          application: {
            gameId: GameIds.VOCAB,
            eventType: 'APP_LAUNCHED',
            details: {
              questId,
              isQuestWorkflow: true,
            },
          },
        },
      });
      launchVocab();
    }
  };

  return (
    <ModalContainer
      role="dialog"
      aria-modal="true"
      aria-labelledby="vocab-launch-modal-title"
      data-testid="vocab-launch-modal"
      data-dd-action-name="vocab-launch-modal"
    >
      <Background src={ModalSmallBackground} alt="" />
      <HeaderText id="vocab-launch-modal-title">RADIO</HeaderText>
      <Decoline src={DecolineMiddle} alt="" />
      <Wave src={WaveImage} alt="" />
      <CloseModalButton
        onClick={onCloseButtonPress}
        data-testid="vocab-launch-modal-close-button"
        data-dd-action-name="vocab-launch-modal:close"
      />
      <VocabGuy src={VocabGuyHappy} alt="Vocab guy" data-testid="vocab-launch-modal-helper-guy" />
      <VocabFrequencyScanning data-testid="vocab-launch-modal-message">Frequency Scanning...</VocabFrequencyScanning>
      <GoButton onClick={onGoButtonPress} ariaLabel="Launch Vocab" actionName="vocab-launch-modal:go" />
      <AutoCompleteButton onClick={() => {
        dispatch(autoCompleteVocabSession(questId, sessionId));
        closeModal();
      }}
      />
    </ModalContainer>
  );
};

VocabLaunchModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default VocabLaunchModal;
